import { LegalDocument } from '@westhetics/legal/react'
import {
  ActionError,
  AttentionBlock,
  BlockTitle,
  Button,
  Checkbox,
  ChoiceRow,
  ConfirmDialog,
  Field,
  FieldGrid,
  formatCurrency,
  formatDate,
  formatPhone,
  FormBlock,
  Input,
  ListRow,
  ListRows,
  MoneyInput,
  PageHeader,
  PageTitle,
  plural,
  Radio,
  RadioGroup,
  ReadOnlyField,
  SavedIndicator,
  Select,
  Skeleton,
  StatusTag,
  StepProgress,
  Surface,
  Textarea,
  titleCase,
  toast,
  useDelayed,
  WaitPhrase,
} from '@westhetics/ui'
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { usePractitionerOptions, useProcedureOptions } from '@/features/anamneses/api'
import { useSignatureProvider } from '@/features/clinic-profile/api'
import { formatDateTime } from '@/features/anamneses/labels'
import { isApiError } from '@/lib/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { routes } from '@/routes'
import { useSendSignatureRequest, type Channels } from '@/features/signatures/api'
import { ChannelPicker } from '@/features/signatures/channel-picker'
import { channelBlocker, firstOpenChannel, sentToast } from '@/features/signatures/labels'
import { ManualLinkActions } from '@/features/signatures/manual-link-actions'
import { usePatient } from '@/features/patients/api'
import { personWords } from '@/features/patients/labels'
import {
  manualMessageOf,
  useSignatureActions,
  type ManualMessage,
} from '@/features/signatures/use-signature-actions'
import {
  FREE_TEXT_MAX_LENGTH,
  openSignedUrl,
  useAppointment,
  useAppointmentOptions,
  useCreateAppointment,
  useDiscardAppointment,
  useGenerate,
  usePdfLink,
  usePreview,
  useUpdateAppointment,
  useZipLink,
  type Appointment,
  type AppointmentOptions,
  type CreateAppointmentInput,
  type Document,
  type Flow,
  type GenerateResult,
  type MissingItem,
  type PreviewResult,
  type UpdateAppointmentInput,
  type Warning,
} from './api'
import {
  ADDITION,
  CATEGORY_LABELS,
  FLOW_CRUMBS,
  FLOW_TITLES,
  FREE_TEXT_REQUIRED,
  RESTRICTED_NOTE,
  RESOLVE_AT_LABELS,
  STEP_LABELS,
  STEP_SUBTITLES,
  additionSubtitle,
  advanceLabel,
  counterLabel,
  freeTextNote,
  generateLabel,
  generatedSentence,
  pagesLabel,
  sendLabel,
} from './labels'

/**
 * Gerar documentos e Documentos avulsos (docs/areas/documentos-e-assinaturas.md, "Gerar documentos"
 * e "Documentos avulsos"; fluxos em "Como funciona · geração"). Arquétipo: fluxo em etapas, três
 * etapas numa coluna de 640px, sidebar à vista. O rascunho é salvo a cada mudança (RN-DOC-35):
 * abandonar nunca é armadilha. A mesma página serve ao tipo `procedure` (com anamnese) e ao
 * `standalone`; `?appointment=<id>` retoma um rascunho ou abre um atendimento gerado; `?addTo=<id>` começa os
 * documentos adicionados a um atendimento já enviado (RN-DOC-57), que vão num envio próprio.
 */

export function GenerateDocumentsPage({ flow }: { flow: Flow }) {
  const { patientId = '' } = useParams()
  const [search, setSearch] = useSearchParams()
  const requested = search.get('appointment')
  const addTo = search.get('addTo')
  // O adicional nasce do atendimento original: a paciente e o tipo vêm dele (RN-DOC-57).
  const start: CreateAppointmentInput = addTo
    ? { type: flow, parentAppointmentId: addTo }
    : { patientId, type: flow }
  const create = useCreateAppointment()
  // O 409 de rascunho existente (RN-DOC-35): a pessoa escolhe retomar ou descartar.
  const [conflict, setConflict] = useState<{ draftId: string; message: string } | null>(null)
  const [appointmentId, setAppointmentId] = useState<string | null>(requested)
  const started = useRef(false)

  useEffect(() => {
    if (appointmentId || started.current) return
    started.current = true
    create.mutate(start, {
      onSuccess: (appointment) => {
        setAppointmentId(appointment.id)
        setSearch({ appointment: appointment.id }, { replace: true })
      },
      onError: (error) => {
        const draftId = isApiError(error) ? error.details.draftId : undefined
        if (typeof draftId === 'string') setConflict({ draftId, message: error.message })
      },
    })
    // Só uma vez por abertura da tela: a criação é idempotente, mas não se repete por rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const query = useAppointment(appointmentId)
  const appointment = query.data

  function resume(id: string) {
    setConflict(null)
    setAppointmentId(id)
    setSearch({ appointment: id }, { replace: true })
  }

  if (!appointment) {
    return (
      <FlowFrame flow={flow} patientName={null} step={1} crumb={addTo ? ADDITION.crumb : undefined}>
        {conflict ? (
          <DraftConflict
            flow={flow}
            patientId={patientId}
            conflict={conflict}
            onResume={resume}
            onDiscarded={() => {
              setConflict(null)
              started.current = false
              create.reset()
              create.mutate(start, { onSuccess: (a) => resume(a.id) })
            }}
          />
        ) : create.isError && !conflict ? (
          <LoadError
            title="Não conseguimos começar os documentos"
            error={create.error}
            onRetry={() => create.mutate(start, { onSuccess: (a) => resume(a.id) })}
          />
        ) : query.isError ? (
          <LoadError
            title="Não conseguimos abrir este atendimento"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <FlowSkeleton />
        )}
      </FlowFrame>
    )
  }

  return <Flow key={appointment.id} flow={flow} appointment={appointment} />
}

// A moldura das três etapas ------------------------------------------------------------------------------

function FlowFrame({
  flow,
  patientName,
  step,
  crumb,
  actions,
  children,
}: {
  flow: Flow
  patientName: string | null
  step: 1 | 2 | 3
  /** A migalha final quando não é a do fluxo ("adicionar documentos"). */
  crumb?: string
  actions?: ReactNode
  children: ReactNode
}) {
  const { patientId = '' } = useParams()
  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Pacientes', href: routes.patients },
          { label: patientName ?? '…', href: routes.patient(patientId) },
          { label: crumb ?? FLOW_CRUMBS[flow] },
        ]}
        actions={actions}
      />
      <Page>
        <div className="mx-auto w-full max-w-[640px]">
          <StepProgress current={step} total={3} label={STEP_LABELS[step - 1]} className="mb-6" />
          {children}
        </div>
      </Page>
    </>
  )
}

function FlowSkeleton() {
  return (
    <div role="status" aria-label="Carregando" className="flex flex-col gap-4">
      <Skeleton className="h-[26px] w-[320px] max-w-full" />
      <Skeleton className="h-[13px] w-[460px] max-w-full" />
      <Skeleton className="mt-2 h-[96px] w-full" />
      <Skeleton className="h-[200px] w-full" />
    </div>
  )
}

/** "Já existe um rascunho… Retome ou descarte antes de começar outro." com as duas saídas. */
function DraftConflict({
  flow,
  patientId,
  conflict,
  onResume,
  onDiscarded,
}: {
  flow: Flow
  patientId: string
  conflict: { draftId: string; message: string }
  onResume: (id: string) => void
  onDiscarded: () => void
}) {
  const discard = useDiscardAppointment()
  const [asking, setAsking] = useState(false)
  return (
    <>
      <AttentionBlock variant="lilac" title="Já existe um rascunho de documentos">
        <p>{conflict.message}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={() => onResume(conflict.draftId)}>Retomar o rascunho</Button>
          <Button variant="outline" onClick={() => setAsking(true)}>
            Descartar e começar outro
          </Button>
          <Button variant="text" asChild>
            <Link to={routes.patientTab(patientId, 'documents')}>Voltar à ficha</Link>
          </Button>
        </div>
        {discard.isError ? (
          <ActionError
            className="mt-4"
            title="Não foi possível descartar o rascunho"
            description={errorMessage(discard.error)}
          />
        ) : null}
      </AttentionBlock>
      <ConfirmDialog
        open={asking}
        onOpenChange={setAsking}
        tone="primary"
        title="Descartar o rascunho?"
        effect={`O rascunho ${flow === 'procedure' ? 'de documentos' : 'de documentos avulsos'} some da ficha e um novo começa do zero. O cadastro da paciente e os documentos já gerados não mudam.`}
        confirmLabel="Descartar e começar outro"
        loading={discard.isPending}
        loadingLabel="Descartando…"
        onConfirm={() =>
          discard.mutate(conflict.draftId, {
            onSuccess: () => {
              setAsking(false)
              onDiscarded()
            },
            onError: () => setAsking(false),
          })
        }
      />
    </>
  )
}

// O fluxo em si ---------------------------------------------------------------------------------------------

function initialStep(appointment: Appointment): 1 | 2 | 3 {
  if (appointment.state === 'generated' || appointment.state === 'sent') return 3
  return appointment.step === 2 ? 2 : 1
}

function Flow({ flow, appointment }: { flow: Flow; appointment: Appointment }) {
  const navigate = useNavigate()
  const [step, setStep] = useState<1 | 2 | 3>(() => initialStep(appointment))
  // Os avisos da última geração (treatment_plan_kept): a consulta é relida e não os traz de volta.
  const [warnings, setWarnings] = useState<Warning[]>([])
  const [discarding, setDiscarding] = useState(false)
  const discard = useDiscardAppointment()
  const patientName = titleCase(appointment.patient.name)
  const isLocked = appointment.state === 'discarded' || appointment.state === 'canceled'
  const crumb = appointment.origin === 'addition' ? ADDITION.crumb : undefined

  // Sair mantém o rascunho (RN-DOC-35); descartar é explícito.
  const leave = (
    <Button
      variant="text"
      size="sm"
      onClick={() => {
        if (appointment.state === 'draft') toast('Rascunho salvo. Retome pela ficha quando quiser.')
        navigate(routes.patientTab(appointment.patient.id, 'documents'))
      }}
    >
      {appointment.state === 'draft' ? 'Sair e manter o rascunho' : 'Voltar à ficha'}
    </Button>
  )

  if (isLocked) {
    return (
      <FlowFrame flow={flow} patientName={patientName} step={1} crumb={crumb} actions={leave}>
        <AttentionBlock variant="lilac" title="Este rascunho foi descartado">
          Comece outro pela ficha da paciente: nada do cadastro mudou.
        </AttentionBlock>
      </FlowFrame>
    )
  }

  return (
    <FlowFrame flow={flow} patientName={patientName} step={step} crumb={crumb} actions={leave}>
      <PageTitle
        eyebrow={patientName}
        title={
          appointment.parent
            ? ADDITION.action
            : flow === 'standalone'
              ? FLOW_TITLES.standalone
              : STEP_LABELS[step - 1]
        }
        subtitle={
          step === 3
            ? 'Prontos.'
            : appointment.parent
              ? additionSubtitle(step, formatDate(new Date(appointment.parent.generatedAt)))
              : STEP_SUBTITLES[flow][step - 1]
        }
      />

      {step === 1 ? (
        <ChooseDocumentsStep
          flow={flow}
          appointment={appointment}
          onNext={() => setStep(2)}
          onDiscard={() => setDiscarding(true)}
        />
      ) : step === 2 ? (
        <ReviewStep
          flow={flow}
          appointment={appointment}
          onBack={() => setStep(1)}
          onGenerated={(result) => {
            setWarnings(result.warnings)
            setStep(3)
          }}
          onDiscard={appointment.state === 'draft' ? () => setDiscarding(true) : undefined}
        />
      ) : (
        <GeneratedStep appointment={appointment} warnings={warnings} onBack={() => setStep(2)} />
      )}

      <ConfirmDialog
        open={discarding}
        onOpenChange={setDiscarding}
        tone="primary"
        title="Descartar o rascunho?"
        effect="O rascunho some da ficha. O cadastro da paciente, as anamneses e os documentos já gerados não mudam."
        confirmLabel="Descartar rascunho"
        loading={discard.isPending}
        loadingLabel="Descartando…"
        onConfirm={() =>
          discard.mutate(appointment.id, {
            onSuccess: () => {
              setDiscarding(false)
              toast('Rascunho descartado.')
              navigate(routes.patientTab(appointment.patient.id, 'documents'))
            },
            onError: (error) => {
              setDiscarding(false)
              toast(errorMessage(error))
            },
          })
        }
      />
    </FlowFrame>
  )
}

// Etapa 1 · Escolha os documentos ---------------------------------------------------------------------------

function ChooseDocumentsStep({
  flow,
  appointment,
  onNext,
  onDiscard,
}: {
  flow: Flow
  appointment: Appointment
  onNext: () => void
  onDiscard: () => void
}) {
  const options = useAppointmentOptions(appointment.id)
  if (options.isError) {
    return (
      <LoadError
        title="Não conseguimos carregar os documentos disponíveis"
        error={options.error}
        onRetry={() => void options.refetch()}
      />
    )
  }
  if (!options.data) {
    return (
      <div role="status" aria-label="Carregando os documentos" className="mt-6 flex flex-col gap-3">
        <Skeleton className="h-[64px] w-full" />
        <Skeleton className="h-[48px] w-full" />
        <Skeleton className="h-[48px] w-full" />
      </div>
    )
  }
  return (
    <ChooseDocumentsForm
      key={appointment.revision}
      flow={flow}
      appointment={appointment}
      options={options.data}
      onNext={onNext}
      onDiscard={onDiscard}
    />
  )
}

function ChooseDocumentsForm({
  flow,
  appointment,
  options,
  onNext,
  onDiscard,
}: {
  flow: Flow
  appointment: Appointment
  options: AppointmentOptions
  onNext: () => void
  onDiscard: () => void
}) {
  const update = useUpdateAppointment(appointment.id)
  const id = useId()
  const [selected, setSelected] = useState<string[]>(() =>
    options.selectable.filter((o) => o.isSelected).map((o) => o.id),
  )
  const [isModelPatient, setIsModelPatient] = useState(appointment.isModelPatient)
  const [anamnesisId, setAnamnesisId] = useState<string | null>(appointment.anamnesis?.id ?? null)
  const fields = update.isError ? fieldErrors(update.error) : {}
  const included = options.included.map((o) => o.id)
  const count = selected.length
  const isProcedure = flow === 'procedure'
  // O avulso traz os documentos do público da pessoa (RN-DOC-42, RN-PAC-41): a lista vem filtrada
  // pela API; aqui só a frase diz para quem ela é.
  const audience = usePatient(appointment.patient.id).data?.audience ?? 'patient'
  const isPerson = !isProcedure && audience !== 'patient'
  const words = personWords(audience)
  // O adicional (RN-DOC-57): a anamnese é a do original, e nada é incluso.
  const isAddition = appointment.origin === 'addition'

  function toggle(templateId: string, checked: boolean) {
    setSelected((current) =>
      checked ? [...current, templateId] : current.filter((x) => x !== templateId),
    )
  }

  function next() {
    update.mutate(
      {
        step: 1,
        revision: appointment.revision,
        templateIds: [...included, ...selected],
        ...(isProcedure ? { isModelPatient } : {}),
        ...(anamnesisId !== (appointment.anamnesis?.id ?? null) ? { anamnesisId } : {}),
      },
      { onSuccess: onNext },
    )
  }

  const anamnesis = options.anamnesis
  return (
    <div className="mt-6 flex flex-col gap-6">
      {isProcedure ? (
        <FormBlock title="Anamnese usada como base">
          {options.availableAnamneses.length > 1 && !isAddition ? (
            <Field
              label="Anamnese"
              hint="A mais recente é sugerida; escolha outra se o procedimento mudou."
            >
              <Select
                value={anamnesisId ?? ''}
                onChange={(e) => setAnamnesisId(e.target.value || null)}
              >
                {options.availableAnamneses.map((a) => (
                  <option key={a.id} value={a.id}>
                    {formatDateTime(a.completedAt)}
                    {a.procedureAtSendTime ? ` · ${a.procedureAtSendTime}` : ''}
                  </option>
                ))}
              </Select>
            </Field>
          ) : anamnesis ? (
            <ReadOnlyField
              label="Anamnese"
              note={null}
              value={`${anamnesis.completedAt ? formatDateTime(anamnesis.completedAt) : ''} · Preenchida${anamnesis.practitionerName ? ` · ${titleCase(anamnesis.practitionerName)}` : ''}`}
            />
          ) : (
            <AttentionBlock variant="plum" title="Nenhuma anamnese preenchida">
              Envie a anamnese e gere os documentos quando ela responder.
              <div className="mt-2">
                <Button variant="text" asChild>
                  <Link to={routes.patientTab(appointment.patient.id, 'anamneses')}>
                    Enviar anamnese
                  </Link>
                </Button>
              </div>
            </AttentionBlock>
          )}
        </FormBlock>
      ) : null}

      {options.included.length > 0 ? (
        <FormBlock title="Inclusos">
          <ListRows>
            {options.included.map((o) => (
              <ListRow
                key={o.id}
                size="lead"
                title={o.name}
                meta={`${CATEGORY_LABELS[o.category]} · versão ${o.version}`}
                trailing={<StatusTag level="done">Incluso</StatusTag>}
              />
            ))}
          </ListRows>
          {options.isModelPatientAvailable && isProcedure ? (
            <ChoiceRow
              className="mt-3"
              htmlFor={`${id}-model`}
              control={
                <Checkbox
                  id={`${id}-model`}
                  checked={isModelPatient}
                  onCheckedChange={(v) => setIsModelPatient(v === true)}
                />
              }
              description="Troca o contrato de prestação pelo contrato de paciente modelo."
            >
              Paciente modelo
            </ChoiceRow>
          ) : null}
        </FormBlock>
      ) : null}

      <FormBlock
        title={
          isAddition
            ? ADDITION.termsTitle
            : isProcedure
              ? 'Termos disponíveis · escolha um'
              : 'Termos disponíveis'
        }
        note={
          isProcedure || isAddition
            ? undefined
            : isPerson
              ? `A lista é a da clínica: os documentos ativos para ${words.many}.`
              : 'A lista é a da clínica: os termos ativos para documentos avulsos.'
        }
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <span className="eyebrow">{counterLabel(count, options.selectable.length)}</span>
          {!isProcedure && options.selectable.length > 1 ? (
            <Button
              variant="text"
              size="sm"
              onClick={() =>
                setSelected(
                  count === options.selectable.length ? [] : options.selectable.map((o) => o.id),
                )
              }
            >
              {count === options.selectable.length ? 'limpar seleção' : 'selecionar todos'}
            </Button>
          ) : null}
        </div>
        {options.selectable.length === 0 ? (
          isAddition ? (
            <AttentionBlock variant="lilac" title={ADDITION.emptyTitle}>
              {ADDITION.emptyBody}
            </AttentionBlock>
          ) : isPerson ? (
            <AttentionBlock variant="plum" title={`Nenhum documento para ${words.many}`}>
              Peça à Westhetics para ativar nesta clínica o contrato ou o termo de {words.many}.
            </AttentionBlock>
          ) : (
            <AttentionBlock variant="plum" title="Nenhum termo disponível">
              Peça à Westhetics o termo do procedimento.
            </AttentionBlock>
          )
        ) : (
          <div className="flex flex-col gap-2">
            {options.selectable.map((o) => (
              <ChoiceRow
                key={o.id}
                htmlFor={`${id}-${o.id}`}
                control={
                  <Checkbox
                    id={`${id}-${o.id}`}
                    checked={selected.includes(o.id)}
                    onCheckedChange={(v) => toggle(o.id, v === true)}
                  />
                }
                description={[
                  o.procedure,
                  o.linkedTo.length > 0
                    ? `vinculado a ${o.linkedTo.map((p) => p.name).join(', ')}`
                    : null,
                  `versão ${o.version}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                {o.name}
              </ChoiceRow>
            ))}
          </div>
        )}
        {fields.templateIds ? (
          <p role="alert" className="font-body text-plum mt-3 text-[12px] leading-[1.5]">
            {fields.templateIds}
          </p>
        ) : null}
      </FormBlock>

      {update.isError && !fields.templateIds ? (
        <ActionError
          title="Não foi possível salvar a escolha"
          description={errorMessage(update.error)}
        />
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="text" onClick={onDiscard}>
          Descartar rascunho
        </Button>
        <Button
          disabled={count === 0}
          loading={update.isPending}
          loadingLabel="Salvando…"
          onClick={next}
        >
          {advanceLabel(count)}
        </Button>
      </div>
    </div>
  )
}

// Etapa 2 · Confira as informações --------------------------------------------------------------------------

interface ReviewForm {
  practitionerId: string | null
  procedureIds: string[]
  /** O que está no campo; `null` é vazio e não é enviado (a API mantém a soma, RN-DOC-20). */
  totalAmountCents: number | null
  imageConsent: 'yes' | 'no' | null
  /** O texto livre como está no campo; vazio limpa (RN-DOC-52). */
  freeText: string
  procedureDate: string | null
  procedureTime: string | null
}

function seedForm(a: Appointment): ReviewForm {
  return {
    practitionerId: a.practitionerId,
    procedureIds: a.procedureIds,
    totalAmountCents:
      a.totalAmountCents ?? a.review?.totalAmountCents.calculatedAmountCents ?? null,
    imageConsent: a.imageConsent === 'not_provided' ? null : a.imageConsent,
    freeText: a.freeText ?? '',
    procedureDate: a.procedureDate,
    procedureTime: a.procedureTime,
  }
}

function sameForm(a: ReviewForm, b: ReviewForm) {
  return (
    a.practitionerId === b.practitionerId &&
    a.procedureIds.join() === b.procedureIds.join() &&
    a.totalAmountCents === b.totalAmountCents &&
    a.imageConsent === b.imageConsent &&
    a.freeText === b.freeText &&
    a.procedureDate === b.procedureDate &&
    a.procedureTime === b.procedureTime
  )
}

/** Só o que mudou vai no `PUT`; valor vazio não vai (a API mantém o que tem). */
function diffForm(before: ReviewForm, after: ReviewForm): Omit<UpdateAppointmentInput, 'revision'> {
  const out: Omit<UpdateAppointmentInput, 'revision'> = { step: 2 }
  if (before.practitionerId !== after.practitionerId) out.practitionerId = after.practitionerId
  if (before.procedureIds.join() !== after.procedureIds.join())
    out.procedureIds = after.procedureIds
  if (before.totalAmountCents !== after.totalAmountCents && after.totalAmountCents !== null) {
    out.totalAmountCents = after.totalAmountCents
  }
  if (before.imageConsent !== after.imageConsent && after.imageConsent)
    out.imageConsent = after.imageConsent
  if (before.freeText !== after.freeText)
    out.freeText = after.freeText.trim() ? after.freeText : null
  if (before.procedureDate !== after.procedureDate) out.procedureDate = after.procedureDate
  if (before.procedureTime !== after.procedureTime) out.procedureTime = after.procedureTime
  return out
}

function ReviewStep({
  flow,
  appointment,
  onBack,
  onGenerated,
  onDiscard,
}: {
  flow: Flow
  appointment: Appointment
  onBack: () => void
  onGenerated: (result: GenerateResult) => void
  onDiscard?: () => void
}) {
  const session = useSession()
  const update = useUpdateAppointment(appointment.id)
  const isRegeneration = appointment.state === 'generated'
  const generate = useGenerate(appointment.id, isRegeneration ? 'regenerate' : 'generate')
  const preview = usePreview(appointment.id)
  const practitioners = usePractitionerOptions()
  const procedures = useProcedureOptions()
  const id = useId()
  const [form, setForm] = useState<ReviewForm>(() => seedForm(appointment))
  const debounced = useDebounced(form, 400)
  // O último estado enviado: é contra ele que o próximo salvamento diz o que mudou.
  const sent = useRef<ReviewForm>(seedForm(appointment))
  const [contact, setContact] = useState({ whatsapp: '', email: '' })
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({})
  const [showPreview, setShowPreview] = useState(false)
  // O erro do texto livre nasce ao sair do campo (RN-DOC-52).
  const [freeTextTouched, setFreeTextTouched] = useState(false)
  const review = appointment.review
  // O adicional (RN-DOC-57): prestadora, procedimentos e valor vêm do original, só leitura.
  const isAddition = appointment.origin === 'addition'
  const practitionerName = (practitioners.data ?? []).find(
    (p) => p.id === form.practitionerId,
  )?.name
  const procedureNames = form.procedureIds
    .map((pid) => (procedures.data ?? []).find((p) => p.id === pid)?.name)
    .filter((name): name is string => Boolean(name))

  // Salvamento automático a cada mudança, 400 ms depois da última (RN-DOC-35).
  useEffect(() => {
    if (sameForm(debounced, sent.current)) return
    const diff = diffForm(sent.current, debounced)
    sent.current = debounced
    update.mutate(
      { revision: appointment.revision, ...diff },
      {
        onSuccess: (saved) => {
          for (const warning of saved.warnings ?? []) toast(warning.message)
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  function saveContact(field: 'whatsapp' | 'email') {
    const value = contact[field].trim()
    if (!value) return
    update.mutate(
      { step: 2, revision: appointment.revision, patient: { [field]: value } },
      {
        onSuccess: () => setContactErrors((e) => ({ ...e, [field]: '' })),
        onError: (error) => {
          const errors = fieldErrors(error)
          setContactErrors((e) => ({
            ...e,
            [field]: errors[`patient.${field}`] ?? errorMessage(error),
          }))
        },
      },
    )
  }

  const conflict = update.isError && isApiError(update.error) && update.error.code === 'conflict'
  const generateFields = generate.isError ? fieldErrors(generate.error) : {}
  const generateDocuments = generate.isError
    ? ((isApiError(generate.error) ? generate.error.details.documents : undefined) as
        { templateId: string; name: string; variables: string[] }[] | undefined)
    : undefined
  // Há mudança ainda não salva: o que está na tela difere do que o debounce já deixou passar.
  const dirty = update.isPending || !sameForm(form, debounced)
  const missing = appointment.missingItems
  const isProcedure = flow === 'procedure'
  const calculated = review?.totalAmountCents.calculatedAmountCents ?? 0
  const freeTextError =
    generateFields.freeText ||
    (update.isError ? fieldErrors(update.error).freeText : undefined) ||
    (freeTextTouched && !form.freeText.trim() ? FREE_TEXT_REQUIRED : null)

  if (!review) {
    return (
      <div role="status" aria-label="Carregando a conferência" className="mt-6 flex flex-col gap-3">
        <Skeleton className="h-[120px] w-full" />
        <Skeleton className="h-[160px] w-full" />
      </div>
    )
  }

  const whatsappShown = review.patient.whatsapp.value
  const emailShown = review.patient.email.value

  return (
    <div className="mt-6 flex flex-col gap-6">
      <div className="flex justify-end">
        <SavedIndicator
          savedAt={appointment.draftSavedAt ? new Date(appointment.draftSavedAt) : null}
          dirty={dirty}
          saving={update.isPending}
        />
      </div>

      {isRegeneration ? (
        <AttentionBlock
          variant="lilac"
          title="Gerar novamente substitui os documentos desta geração"
        >
          Os anteriores ficam no histórico como cancelados. Corrija o que precisa e gere de novo; a
          versão vigente de cada template é usada (RN-DOC-31).
        </AttentionBlock>
      ) : null}

      {conflict ? (
        <ActionError
          title="Este rascunho foi alterado em outra aba"
          description="Recarregue para continuar: o que foi salvo lá vale."
          actions={
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Recarregar
            </Button>
          }
        />
      ) : update.isError && !conflict && Object.keys(fieldErrors(update.error)).length === 0 ? (
        <ActionError title="Não foi possível salvar" description={errorMessage(update.error)} />
      ) : null}

      <FormBlock
        title="Paciente"
        note="Vem do cadastro. Para corrigir nome, documento ou endereço, edite a ficha."
      >
        <FieldGrid>
          <ReadOnlyField label="Nome" note={null} value={titleCase(review.patient.name.value)} />
          <ReadOnlyField
            label="Documento"
            note={null}
            value={review.patient.idDocument.value ?? 'Não informado'}
          />
          <ReadOnlyField
            label="Nascimento"
            note={null}
            value={
              review.patient.birthDate.value
                ? formatDate(new Date(`${review.patient.birthDate.value}T12:00:00`))
                : 'Não informado'
            }
          />
          {review.patient.whatsapp.isEditable ? (
            <Field
              label="WhatsApp"
              hint="Obrigatório: é o canal principal de envio. Grava no cadastro."
              error={contactErrors.whatsapp || null}
            >
              <Input
                type="tel"
                inputMode="tel"
                placeholder="11 99999-0000"
                value={contact.whatsapp}
                onChange={(e) => setContact((c) => ({ ...c, whatsapp: e.target.value }))}
                onBlur={() => saveContact('whatsapp')}
              />
            </Field>
          ) : (
            <ReadOnlyField
              label="WhatsApp"
              note={null}
              value={whatsappShown ? formatPhone(whatsappShown) : 'Não informado'}
            />
          )}
          {review.patient.email.isEditable ? (
            <Field
              label="E-mail"
              optional
              hint="Grava no cadastro."
              error={contactErrors.email || null}
            >
              <Input
                type="email"
                inputMode="email"
                value={contact.email}
                onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                onBlur={() => saveContact('email')}
              />
            </Field>
          ) : (
            <ReadOnlyField label="E-mail" note={null} value={emailShown ?? 'Não informado'} />
          )}
          <ReadOnlyField
            label="Endereço"
            note={null}
            value={review.patient.address.value ?? 'Não informado'}
            className="tablet:col-span-2"
          />
          {appointment.patient.isMinor ? (
            <ReadOnlyField
              label="Responsável"
              note={null}
              value={
                review.patient.guardian.value
                  ? titleCase(review.patient.guardian.value.name)
                  : 'Não cadastrada'
              }
            />
          ) : null}
        </FieldGrid>
        <div className="mt-3">
          <Button variant="text" size="sm" asChild>
            <Link to={routes.patientTab(appointment.patient.id, 'details')}>editar cadastro</Link>
          </Button>
        </div>
      </FormBlock>

      {isAddition ? (
        <FormBlock
          title="Do atendimento original"
          note="Profissional, procedimentos e valor são os do atendimento original e não se editam aqui."
        >
          <FieldGrid>
            <ReadOnlyField
              label="Profissional responsável"
              note={null}
              value={practitionerName ? titleCase(practitionerName) : '—'}
            />
            <ReadOnlyField
              label="Valor total"
              note={null}
              value={
                form.totalAmountCents === null ? '—' : formatCurrency(form.totalAmountCents / 100)
              }
            />
          </FieldGrid>
          <div className="mt-4">
            <ReadOnlyField
              label="Procedimentos"
              note={null}
              value={procedureNames.length > 0 ? procedureNames.join(', ') : '—'}
            />
          </div>
        </FormBlock>
      ) : (
        <>
          {isProcedure || review.practitionerId.isEditable ? (
            <FormBlock
              title="Profissional e procedimento"
              note="Select, nunca texto livre: o valor vem do cadastro."
            >
              <FieldGrid>
                <Field
                  label="Profissional responsável"
                  error={generateFields.practitionerId || null}
                >
                  <Select
                    value={form.practitionerId ?? ''}
                    placeholder="Selecione"
                    disabled={!review.practitionerId.isEditable || practitioners.isPending}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, practitionerId: e.target.value || null }))
                    }
                  >
                    {(practitioners.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {titleCase(p.name)}
                      </option>
                    ))}
                  </Select>
                </Field>
              </FieldGrid>
              <div className="mt-4">
                <span className="eyebrow">Procedimentos</span>
                {procedures.isPending ? (
                  <Skeleton className="mt-2 h-[48px] w-full" />
                ) : (procedures.data ?? []).length === 0 ? (
                  <p className="font-body text-secondary mt-2 text-[12px] leading-[1.6]">
                    Nenhum procedimento no catálogo da clínica.{' '}
                    {session.canManageClinic ? (
                      <Link to={routes.admin.procedures}>Cadastrar procedimentos</Link>
                    ) : (
                      'Peça à gestora.'
                    )}
                  </p>
                ) : (
                  <div className="mt-2 flex flex-col gap-2">
                    {(procedures.data ?? []).map((p) => (
                      <ChoiceRow
                        key={p.id}
                        htmlFor={`${id}-proc-${p.id}`}
                        control={
                          <Checkbox
                            id={`${id}-proc-${p.id}`}
                            checked={form.procedureIds.includes(p.id)}
                            onCheckedChange={(v) =>
                              setForm((f) => ({
                                ...f,
                                procedureIds:
                                  v === true
                                    ? [...f.procedureIds, p.id]
                                    : f.procedureIds.filter((x) => x !== p.id),
                              }))
                            }
                          />
                        }
                      >
                        {p.name}
                      </ChoiceRow>
                    ))}
                  </div>
                )}
                {generateFields.procedureIds ? (
                  <p role="alert" className="font-body text-plum mt-2 text-[12px] leading-[1.5]">
                    {generateFields.procedureIds}
                  </p>
                ) : null}
              </div>
            </FormBlock>
          ) : null}

          <FormBlock
            title="Valor total"
            note="Nasce da soma dos procedimentos. Pode ser ajustado; o ajuste fica registrado."
          >
            <FieldGrid>
              <Field
                label="Valor total"
                hint={
                  review.totalAmountCents.isManuallyEdited
                    ? `Valor ajustado à mão · calculado: ${formatCurrency(calculated / 100)}`
                    : undefined
                }
                error={generateFields.totalAmountCents || null}
              >
                <MoneyInput
                  value={form.totalAmountCents}
                  onChange={(cents) => setForm((f) => ({ ...f, totalAmountCents: cents }))}
                />
              </Field>
              <ReadOnlyField
                label="Condição de pagamento"
                note="· gerada"
                value={review.paymentTerms.value || 'Escolha um procedimento'}
              />
            </FieldGrid>
          </FormBlock>
        </>
      )}

      {isProcedure ? (
        <FormBlock
          title="Uso de imagem"
          note="Resposta deste atendimento. Atualiza o cadastro da paciente nesta clínica."
        >
          <RadioGroup
            value={form.imageConsent ?? ''}
            onValueChange={(v) => setForm((f) => ({ ...f, imageConsent: v as 'yes' | 'no' }))}
            aria-label="Uso de imagem"
          >
            <ChoiceRow
              htmlFor={`${id}-img-yes`}
              control={<Radio id={`${id}-img-yes`} value="yes" />}
            >
              Autoriza
            </ChoiceRow>
            <ChoiceRow htmlFor={`${id}-img-no`} control={<Radio id={`${id}-img-no`} value="no" />}>
              Não autoriza
            </ChoiceRow>
          </RadioGroup>
          {generateFields.imageConsent ? (
            <p role="alert" className="font-body text-plum mt-2 text-[12px] leading-[1.5]">
              {generateFields.imageConsent}
            </p>
          ) : null}
        </FormBlock>
      ) : null}

      {review.freeText ? (
        <FormBlock
          title="Texto livre"
          note={freeTextNote(review.freeText.templates.map((t) => t.name))}
        >
          <Field
            label="Texto livre"
            meta={`${form.freeText.length}/${FREE_TEXT_MAX_LENGTH}`}
            error={freeTextError || null}
            hint="Linha em branco separa parágrafos."
          >
            <Textarea
              rows={6}
              maxLength={FREE_TEXT_MAX_LENGTH}
              value={form.freeText}
              onChange={(e) => setForm((f) => ({ ...f, freeText: e.target.value }))}
              onBlur={() => setFreeTextTouched(true)}
            />
          </Field>
        </FormBlock>
      ) : null}

      <FormBlock
        title="Data e horário do procedimento"
        note="Opcionais. Com a data, o atendimento entra na agenda do dia."
      >
        <FieldGrid>
          <Field label="Data" optional>
            <Input
              type="date"
              value={form.procedureDate ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, procedureDate: e.target.value || null }))}
            />
          </Field>
          <Field label="Horário" optional>
            <Input
              type="time"
              value={form.procedureTime ?? ''}
              disabled={!form.procedureDate}
              onChange={(e) => setForm((f) => ({ ...f, procedureTime: e.target.value || null }))}
            />
          </Field>
        </FieldGrid>
      </FormBlock>

      {missing.total > 0 ? (
        <MissingItemsBlock
          items={missing.items}
          message={missing.message}
          patientId={appointment.patient.id}
          canManage={session.canManageClinic}
          onStep1={onBack}
        />
      ) : null}

      {generate.isError && Object.keys(generateFields).length === 0 ? (
        <ActionError
          title={
            isApiError(generate.error) && generate.error.status === 503
              ? 'Os PDFs não saíram agora'
              : 'Não foi possível gerar os documentos'
          }
          description={errorMessage(generate.error)}
        />
      ) : null}
      {generateDocuments && generateDocuments.length > 0 ? (
        <AttentionBlock variant="plum" title="Variáveis sem valor nos documentos">
          {generateDocuments.map((d) => (
            <div key={d.templateId}>
              · {d.name}: {d.variables.join(', ')}
            </div>
          ))}
        </AttentionBlock>
      ) : null}

      {showPreview ? (
        <PreviewPanel preview={preview} onClose={() => setShowPreview(false)} />
      ) : null}

      <WaitPhrase moment="generating" active={generate.isPending} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={onBack}>
            Voltar
          </Button>
          <Button
            variant="text"
            loading={preview.isPending}
            loadingLabel="Montando a prévia…"
            onClick={() => {
              setShowPreview(true)
              preview.mutate(undefined)
            }}
          >
            Pré-visualizar
          </Button>
          {onDiscard ? (
            <Button variant="text" onClick={onDiscard}>
              Descartar rascunho
            </Button>
          ) : null}
        </div>
        <Button
          disabled={!appointment.canGenerate || update.isPending || dirty}
          loading={generate.isPending}
          loadingLabel="Gerando…"
          onClick={() => generate.mutate(appointment.generation, { onSuccess: onGenerated })}
        >
          {generateLabel(missing.total, isRegeneration)}
        </Button>
      </div>
    </div>
  )
}

/** "Faltam 2 campos para gerar: profissional e procedimento." e onde cada um se resolve. */
function MissingItemsBlock({
  items,
  message,
  patientId,
  canManage,
  onStep1,
}: {
  items: MissingItem[]
  message: string | null
  patientId: string
  canManage: boolean
  onStep1: () => void
}) {
  function exit(item: MissingItem): ReactNode {
    switch (item.resolveAt) {
      case 'patient_record':
        return <Link to={routes.patientTab(patientId, 'details')}>editar cadastro</Link>
      case 'clinic_profile':
        return canManage ? <Link to={routes.admin.clinicProfile}>abrir Empresa</Link> : null
      case 'catalog':
        return canManage ? (
          <Link
            to={
              item.field === 'practitionerId' ? routes.admin.practitioners : routes.admin.procedures
            }
          >
            abrir cadastro
          </Link>
        ) : null
      case 'step1':
        return (
          <Button variant="text" size="sm" onClick={onStep1}>
            voltar à etapa 1
          </Button>
        )
      default:
        return null
    }
  }
  return (
    <AttentionBlock variant="plum" title={message ?? 'Faltam dados para gerar'}>
      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li key={item.field} className="flex flex-wrap items-baseline gap-x-2">
            <span>{item.message}</span>
            <span className="text-secondary text-[12px]">{RESOLVE_AT_LABELS[item.resolveAt]}</span>
            {item.templates.length > 0 ? (
              <span className="text-secondary text-[12px]">
                · em {item.templates.map((t) => t.name).join(', ')}
              </span>
            ) : null}
            {exit(item)}
          </li>
        ))}
      </ul>
    </AttentionBlock>
  )
}

/** A prévia (RN-DOC-32): cada documento como a paciente vai ler, com os dados atuais. */
function PreviewPanel({
  preview,
  onClose,
}: {
  preview: ReturnType<typeof usePreview>
  onClose: () => void
}) {
  const data: PreviewResult | undefined = preview.data
  return (
    <Surface className="max-desk:px-4">
      <div className="flex items-center justify-between gap-3">
        <BlockTitle
          title="Prévia"
          note="Como a paciente vai ler, com os dados de agora. Nada é salvo; o PDF só nasce na geração."
        />
        <Button variant="text" size="sm" onClick={onClose}>
          Fechar prévia
        </Button>
      </div>
      {preview.isError ? (
        <ActionError
          className="mt-4"
          title="Não conseguimos montar a prévia"
          description={errorMessage(preview.error)}
        />
      ) : !data ? (
        <div role="status" aria-label="Montando a prévia" className="mt-4">
          <Skeleton className="h-[240px] w-full" />
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-6">
          {data.documents.map((d) => (
            <section key={d.templateId} aria-label={d.name}>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-display text-ink text-[14px]">{d.name}</span>
                <span className="font-body text-secondary text-[12px]">versão {d.version}</span>
              </div>
              {d.isRestricted ? (
                <p className="font-body text-secondary text-[12px] leading-[1.6]">
                  {RESTRICTED_NOTE}
                </p>
              ) : (
                <div className="border-hairline bg-surface border px-4 py-4">
                  <LegalDocument
                    markdown={d.resolvedMarkdown ?? ''}
                    anamnesis={d.anamnesis ?? null}
                  />
                </div>
              )}
              {d.diagnostics.length > 0 ? (
                <ul className="mt-2 flex flex-col gap-1">
                  {d.diagnostics.map((x, i) => (
                    <li key={i} className="font-body text-plum text-[12px] leading-[1.5]">
                      {x.message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </div>
      )}
    </Surface>
  )
}

// Etapa 3 · Documentos gerados -----------------------------------------------------------------------------

function GeneratedStep({
  appointment,
  warnings,
  onBack,
}: {
  appointment: Appointment
  warnings: Warning[]
  onBack: () => void
}) {
  const pdf = usePdfLink()
  const zip = useZipLink()
  const [busyId, setBusyId] = useState<string | null>(null)
  const documents = appointment.documents
  const live = documents.filter((d) => d.status !== 'canceled' && d.status !== 'deleted')
  const downloadable = live.filter((d) => !d.isRestricted)
  const patientName = titleCase(appointment.patient.name)
  const allGenerated = live.length > 0 && live.every((d) => d.status === 'generated')
  const procedures = useMemo(
    () => appointment.procedures.map((p) => p.name),
    [appointment.procedures],
  )

  function download(doc: Document) {
    setBusyId(doc.id)
    pdf.mutate(
      { appointmentId: appointment.id, documentId: doc.id },
      {
        onSuccess: (link) => openSignedUrl(link.url),
        onError: (error) => toast(errorMessage(error)),
        onSettled: () => setBusyId(null),
      },
    )
  }

  function downloadAll() {
    zip.mutate(appointment.id, {
      onSuccess: (link) => {
        openSignedUrl(link.url)
        if (link.omitted.length > 0) toast(link.omitted[0]!.reason)
      },
      onError: (error) => toast(errorMessage(error)),
    })
  }

  return (
    <div className="mt-6 flex flex-col gap-6">
      <p className="font-display text-ink text-lead leading-[1.3]">
        {generatedSentence(live.length, patientName)}
      </p>
      <p className="font-body text-secondary text-[12px] leading-[1.6]">
        {[
          appointment.generatedAt ? `Gerados em ${formatDateTime(appointment.generatedAt)}` : null,
          procedures.length > 0 ? procedures.join(', ') : null,
          appointment.practitioner ? titleCase(appointment.practitioner.name) : null,
          appointment.totalAmountCents !== null
            ? formatCurrency(appointment.totalAmountCents / 100)
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        {appointment.generation > 1 ? ` · geração ${appointment.generation}` : ''}
      </p>

      {warnings.map((w) => (
        <AttentionBlock key={w.code} variant="lilac">
          {w.message}
        </AttentionBlock>
      ))}

      <ListRows>
        {live.map((doc) => (
          <ListRow
            key={doc.id}
            size="lead"
            eyebrow={`${String(doc.position).padStart(2, '0')} · versão ${doc.version ?? '—'}`}
            title={doc.name}
            meta={
              doc.isRestricted
                ? RESTRICTED_NOTE
                : [pagesLabel(doc.pageCount), doc.shortHash ? `hash ${doc.shortHash}` : null]
                    .filter(Boolean)
                    .join(' · ')
            }
            critical={doc.level === 'critical'}
            trailing={
              <div className="flex flex-col items-end gap-2">
                <StatusTag level={doc.level}>{doc.label}</StatusTag>
                {doc.isRestricted ? null : (
                  <Button
                    variant="text"
                    size="sm"
                    loading={busyId === doc.id}
                    loadingLabel="Preparando…"
                    onClick={() => download(doc)}
                  >
                    Baixar PDF
                  </Button>
                )}
              </div>
            }
          />
        ))}
      </ListRows>

      {appointment.previous.length > 0 ? (
        <details>
          <summary className="font-body text-secondary cursor-pointer text-[12px]">
            ver anteriores (
            {plural(appointment.previous.length, 'documento cancelado', 'documentos cancelados')})
          </summary>
          <ListRows className="mt-2">
            {appointment.previous.map((doc) => (
              <ListRow
                key={doc.id}
                size="lead"
                title={doc.name}
                meta={`geração ${doc.generation} · versão ${doc.version ?? '—'}`}
                trailing={<StatusTag level="done">{doc.label}</StatusTag>}
              />
            ))}
          </ListRows>
        </details>
      ) : null}

      {downloadable.length > 1 ? (
        <div>
          <Button
            variant="outline"
            loading={zip.isPending}
            loadingLabel="Reunindo os PDFs…"
            onClick={downloadAll}
          >
            Baixar tudo
          </Button>
        </div>
      ) : null}

      {appointment.treatmentPlanId ? (
        <AttentionBlock variant="lilac" title="Plano de tratamento criado">
          <Link to={routes.treatmentPlan(appointment.treatmentPlanId)}>abrir</Link>
        </AttentionBlock>
      ) : null}

      {allGenerated ? (
        <SendBlock appointment={appointment} count={live.length} onBack={onBack} />
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" disabled>
            Documentos já enviados
          </Button>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" asChild>
              <Link to={routes.signature(appointment.id)}>Ver em Assinaturas</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to={routes.patientTab(appointment.patient.id, 'documents')}>
                Ir para a ficha
              </Link>
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Enviar para assinatura (RN-ASS-01 a 03, RN-ASS-14): o canal, o botão e o aviso com Desfazer por 10
 * segundos; depois, a lista de Assinaturas em "Aguardando paciente" (P-ASS-02). No WhatsApp assistido
 * (P-ASS-21), a mensagem fica pronta para a pessoa mandar pelo WhatsApp dela antes de seguir.
 */
function SendBlock({
  appointment,
  count,
  onBack,
}: {
  appointment: Appointment
  count: number
  onBack: () => void
}) {
  const navigate = useNavigate()
  const send = useSendSignatureRequest()
  const signatureActions = useSignatureActions()
  // Na Autentique (RN-ASS-36) quem entrega é ela: o seletor diz isso em vez do WhatsApp assistido.
  const provider = useSignatureProvider().data?.provider
  const contact = appointment.contacts
  const [channels, setChannels] = useState<Channels>(() => firstOpenChannel(contact))
  const [manual, setManual] = useState<ManualMessage | null>(null)
  const blocker = channelBlocker(channels, contact)
  const showWait = useDelayed(send.isPending)

  function submit() {
    send.mutate(
      { appointmentId: appointment.id, channels },
      {
        onSuccess: (result) => {
          toast(sentToast(count, result.patientFirstName), {
            undo: signatureActions.undo(result.actionId),
          })
          const message = manualMessageOf(result.signatureRequest, result.patientName)
          if (message) setManual(message)
          else navigate(routes.signaturesFiltered('awaiting_patient'))
        },
      },
    )
  }

  if (manual) {
    return (
      <div className="flex flex-col gap-5">
        <AttentionBlock
          variant="lilac"
          title={`Mande o link para ${manual.patientName.split(' ')[0]} pelo seu WhatsApp`}
        >
          O WhatsApp ainda não sai pelo sistema: a mensagem abaixo já está pronta, com o link. Abra
          o WhatsApp com ela ou copie e cole na conversa. O e-mail, quando escolhido, sai sozinho.
        </AttentionBlock>
        <pre className="border-hairline-strong bg-surface font-body text-ink border px-[14px] py-3 text-[12px] leading-[1.6] font-light whitespace-pre-wrap">
          {manual.text}
        </pre>
        <ManualLinkActions
          message={manual}
          onOpen={signatureActions.openWhatsapp}
          onCopyLink={(link) => void signatureActions.copyLink(link)}
          onCopyMessage={(text) => void signatureActions.copyMessage(text)}
        />
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => navigate(routes.signaturesFiltered('awaiting_patient'))}>
            Ir para Assinaturas
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="eyebrow">Enviar para assinatura por</div>
      <ChannelPicker
        value={channels}
        onChange={setChannels}
        contact={contact}
        whatsappMode={provider === 'autentique' ? 'provider' : 'manual'}
        provider={provider}
        label="Canal"
      />
      <p className="font-body text-secondary text-[12px] leading-[1.6] font-light">
        Entrega e leitura ficam visíveis em Assinaturas. Uma assinatura da paciente vale para todos
        os documentos; o link vale por 7 dias e pode ser reenviado sem gerar documento novo.
      </p>
      {send.isError && !fieldErrors(send.error).whatsapp && !fieldErrors(send.error).email ? (
        <ActionError title="Não foi possível enviar" description={errorMessage(send.error)} />
      ) : null}
      {fieldErrors(send.error).whatsapp ||
      fieldErrors(send.error).email ||
      fieldErrors(send.error).guardian ? (
        <ActionError
          title="Falta um contato na ficha"
          description={
            fieldErrors(send.error).whatsapp ??
            fieldErrors(send.error).email ??
            fieldErrors(send.error).guardian
          }
          actions={
            <Button variant="outline" size="sm" asChild>
              <Link to={routes.patientTab(appointment.patient.id, 'details')}>
                Corrigir na ficha
              </Link>
            </Button>
          }
        />
      ) : null}
      {showWait ? <WaitPhrase moment="sending" align="left" /> : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={onBack}>
          Voltar e corrigir
        </Button>
        <div className="flex flex-wrap gap-3">
          <Button
            disabled={blocker !== null}
            loading={send.isPending}
            loadingLabel="Enviando…"
            onClick={submit}
          >
            {blocker ? `${sendLabel(count)} · ${blocker.replace(/\.$/, '')}` : sendLabel(count)}
          </Button>
          <Button variant="outline" asChild>
            <Link to={routes.patientTab(appointment.patient.id, 'documents')}>Ir para a ficha</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
